import { GearSix, Plus } from '@phosphor-icons/react'
import { Button } from 'antd'
import { HTMLAttributes, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { truncateText } from '@/components/CalculationComponent/utils/truncateText'
import { ChartTooltipModel } from '@/domain/model/measures'
import { useUpdateURLSearchParams } from '@/hooks/useQuery'
import useSearchParams from '@/hooks/useSearchParams'
import { useLoadMeasuresByFormAndClientId } from '@/queries/measures/useLoadMeasuresByFormAndClientId'
import cn from '@/utils/tailwind/cn'

import { LevelsModal } from '../Components/LevelsModal'
import { getChartFormattedData } from '../utils/getChartFormattedData'
import { getCurrentQuestionById } from '../utils/getCurrentQuestionById'
import { getMaxThreshold } from '../utils/getMaxThreshold'
import { getYTicks } from '../utils/getYTicks'
import { transformLevelsToCategories } from '../utils/transformLevelsToCategories'

const buttonLevelsOptions = {
  true: (
    <span className="flex gap-1">
      <GearSix className="text-lg" /> Customize Levels
    </span>
  ),
  false: (
    <span className="flex gap-1">
      <Plus className="text-lg" weight="bold" /> Add Levels
    </span>
  ),
}

export const ChartView = ({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) => {
  const { clientId } = useParams<{ clientId?: string }>()
  const searchParams = useSearchParams()
  const updateParams = useUpdateURLSearchParams()

  const formId = searchParams.get('formId') || ''
  const questionId = searchParams.get('question')
  const isOpenModal = searchParams.get('assessment_levels') === 'open'

  const { data: measureData } = useLoadMeasuresByFormAndClientId(
    formId,
    clientId || ''
  )

  const currentQuestion = getCurrentQuestionById({
    data: measureData,
    questionId,
  })
  const levels = transformLevelsToCategories(currentQuestion?.levels)
  const maxThreshold = getMaxThreshold(currentQuestion)

  const hasLevels = Boolean(currentQuestion?.levels?.length)

  const chartData = useMemo(
    () => getChartFormattedData({ data: measureData, questionId }),
    [measureData, questionId]
  )

  const CustomTooltip = (props: any) => {
    const { active, payload, label } = props as ChartTooltipModel
    if (active && payload && payload.length) {
      const scoreValue = payload[0].value

      return (
        <div className="rounded border bg-white p-2 shadow">
          <p className="text-sm font-medium">{label}</p>
          <p className="text-sm">
            Score: <span className="font-semibold">{scoreValue}</span>
            {levels ? (
              <span className="ml-1 text-xs">({levels(scoreValue).label})</span>
            ) : null}
          </p>
          {levels ? (
            <p className="text-sm">
              Level:{' '}
              <span className="font-semibold">{levels(scoreValue).label}</span>
            </p>
          ) : null}
        </div>
      )
    }
    return null
  }

  const getYDomain = () => [0, maxThreshold]

  const yTicks = getYTicks(maxThreshold)

  const severityZones = useMemo(() => {
    if (!currentQuestion?.levels.length) {
      return []
    }

    return currentQuestion.levels.map((item) => ({
      y: item.threshold,
      label: item.label,
      color: item.color,
    }))
  }, [currentQuestion])

  return (
    <>
      <div
        className={cn(
          'w-full flex-col gap-4 rounded border border-gray-50 bg-white px-4 pb-4 pt-3',
          className
        )}
        {...props}
      >
        <div className="mb-2.5 flex w-full justify-end">
          <Button
            className="text-sm text-gray-900"
            onClick={() => updateParams(['assessment_levels', 'open'])}
          >
            {
              buttonLevelsOptions[
                String(hasLevels) as keyof typeof buttonLevelsOptions
              ]
            }
          </Button>
        </div>
        <div id="measures-chart-container">
          <ResponsiveContainer height={460} width="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis
                domain={getYDomain()}
                label={{
                  value: truncateText(currentQuestion?.text || '', 40),
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle' },
                }}
                tick={{ fontSize: 12 }}
                ticks={yTicks}
              />

              <Tooltip content={<CustomTooltip />} />
              <Legend />

              {severityZones?.map((zone) => (
                <ReferenceLine
                  key={zone.y}
                  label={{
                    value: zone.label,
                    position: 'insideLeft',
                    style: { fill: zone.color, fontSize: 10 },
                  }}
                  stroke={zone.color}
                  strokeDasharray="3 3"
                  y={zone.y}
                />
              ))}

              <Line
                connectNulls
                activeDot={{ r: 6 }}
                dataKey="score"
                dot={{ r: 4 }}
                name={currentQuestion?.text || ''}
                stroke="#3B82F6"
                strokeWidth={2}
                type="monotone"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {isOpenModal ? <LevelsModal /> : null}
    </>
  )
}
